// Session 6 PR3 — full-page interview prep chat.
//
// Live mode: pre-interview coaching. Debrief mode: post-interview reflection.
// Mode flips via a "Mark interview complete" button that POSTs
// /prep/mark-debrief (the agent broadcasts the new state via setState so any
// other tab re-renders too).
//
// History persistence is the agent's job: setState frames carry the full
// `messages` array on every turn, so on page reload the WS connect replays
// the latest snapshot and we render off `agent.state?.messages`.

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useAgent } from "agents/react";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Send,
  Sparkles,
} from "lucide-react";
import type {
  InterviewPrepMessage,
  InterviewPrepState,
} from "@resume-cloudflare/shared";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";
import { Textarea } from "../components/ui/Textarea";
import {
  useMarkDebrief,
  usePrepState,
  useSendPrepChat,
} from "../lib/interview_prep";

export default function InterviewPrep() {
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId ?? null;

  if (!applicationId) {
    return (
      <section className="mx-auto max-w-4xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Missing application id"
          description="This page expects /applications/:id/prep."
        />
      </section>
    );
  }

  return <InterviewPrepInner applicationId={applicationId} />;
}

function InterviewPrepInner({ applicationId }: { applicationId: string }) {
  const stateQ = usePrepState(applicationId);
  const send = useSendPrepChat(applicationId);
  const mark = useMarkDebrief(applicationId);
  const [draft, setDraft] = useState("");

  // Live agent state via PartySocket WS (proxied through /prep/ws so
  // requireSession gates the upgrade). The SDK's `useAgent` hook auto-
  // reconnects + replays the last setState frame on reconnect; we pull
  // messages + mode + pending_turn off here.
  const agent = useAgent<InterviewPrepState>({
    agent: "interview-prep-agent",
    name: applicationId,
    basePath: `/api/applications/${applicationId}/prep/ws`,
  });

  const live = agent.state ?? null;
  const meta = stateQ.data?.session ?? null;
  const messages = live?.messages ?? [];
  const mode = live?.mode ?? meta?.mode ?? "live";
  const pendingTurn = live?.pending_turn ?? false;
  // turns_count is also surfaced via WS; D1 mirror used as fallback before
  // the WS connects.
  const turnsCount = live?.turns_count ?? meta?.turns_count ?? 0;

  // Auto-scroll to bottom on new messages / pending state changes.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, pendingTurn]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || pendingTurn || send.isPending) return;
    setDraft("");
    try {
      await send.mutateAsync({ message: trimmed });
    } catch {
      // Restore the draft on failure so the user doesn't lose their input.
      setDraft(trimmed);
    }
  }

  if (stateQ.isLoading && !meta) {
    return (
      <section className="mx-auto flex h-[calc(100dvh-64px)] max-w-4xl flex-col gap-3 p-4 sm:p-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </section>
    );
  }

  if (stateQ.error || !meta) {
    return (
      <section className="mx-auto max-w-4xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Couldn't load prep session"
          description="The application id may be invalid or you don't have access."
        />
      </section>
    );
  }

  return (
    <section className="mx-auto flex h-[calc(100dvh-64px)] max-w-4xl flex-col p-4 sm:p-6">
      <PrepHeader
        applicationId={applicationId}
        mode={mode}
        company={meta.company}
        role={meta.role}
        jobUrl={meta.job_url}
        interviewAt={meta.interview_at}
        turnsCount={turnsCount}
        onMarkComplete={() => mark.mutate()}
        markPending={mark.isPending}
      />

      <div
        ref={scrollRef}
        className="my-3 flex-1 overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-950/40 p-3"
      >
        {messages.length === 0 ? (
          <EmptyChat mode={mode} />
        ) : (
          <ol className="space-y-3">
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {pendingTurn ? <TypingIndicator /> : null}
          </ol>
        )}
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void onSubmit(e as unknown as React.FormEvent);
            }
          }}
          placeholder={
            mode === "live"
              ? "Ask the coach… ('How do I answer system-design questions for this role?')"
              : "Reflect with the coach… ('What follow-ups should I send to the panel?')"
          }
          className="min-h-[88px]"
          disabled={pendingTurn || send.isPending}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-neutral-500">
            ⌘/Ctrl + Enter to send. {turnsCount} turns so far.
          </p>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={!draft.trim() || pendingTurn || send.isPending}
            loading={send.isPending}
          >
            <Send size={14} className="mr-1.5" /> Send
          </Button>
        </div>
      </form>
    </section>
  );
}

function PrepHeader({
  applicationId,
  mode,
  company,
  role,
  jobUrl,
  interviewAt,
  turnsCount,
  onMarkComplete,
  markPending,
}: {
  applicationId: string;
  mode: "live" | "debrief";
  company: string | null;
  role: string;
  jobUrl: string;
  interviewAt: number | null;
  turnsCount: number;
  onMarkComplete: () => void;
  markPending: boolean;
}) {
  return (
    <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-800 pb-3">
      <div className="min-w-0">
        <Link
          to={`/applications`}
          className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-300"
        >
          <ArrowLeft size={12} /> Back to Applications
        </Link>
        <h1 className="mt-1 truncate text-lg font-semibold text-neutral-100">
          {mode === "live" ? "Interview prep" : "Interview debrief"}
          <span className="text-neutral-500"> · {role}</span>
        </h1>
        <p className="mt-0.5 text-xs text-neutral-500">
          {company ?? "Company"} ·{" "}
          <a
            href={jobUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-300 hover:underline"
          >
            posting
          </a>
          {interviewAt ? <> · interview {dateLabel(interviewAt)}</> : null}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Pill tone={mode === "live" ? "accent" : "warning"}>
          {mode === "live" ? (
            <>
              <Sparkles size={10} className="mr-1" /> Live coaching
            </>
          ) : (
            <>
              <CheckCircle2 size={10} className="mr-1" /> Debriefing
            </>
          )}
        </Pill>
        {mode === "live" ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={onMarkComplete}
            loading={markPending}
            title="Switch to post-interview reflection"
          >
            Mark interview complete
          </Button>
        ) : null}
        <span className="hidden sm:inline text-[10px] text-neutral-600">
          app {applicationId.slice(0, 8)}
        </span>
        <span className="sr-only">{turnsCount} turns so far</span>
      </div>
    </header>
  );
}

function EmptyChat({ mode }: { mode: "live" | "debrief" }) {
  return (
    <div className="grid min-h-[200px] place-items-center text-center">
      <div className="max-w-md space-y-2 text-neutral-500">
        <Sparkles size={18} className="mx-auto text-accent-300" />
        {mode === "live" ? (
          <>
            <p className="text-sm text-neutral-300">Ask the coach a question.</p>
            <p className="text-xs">
              Each turn pulls fresh context from your career corpus. Try:{" "}
              <em>
                "Walk me through a STAR answer for the toughest enterprise deal
                I closed."
              </em>
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-neutral-300">Debrief mode.</p>
            <p className="text-xs">
              How did the interview go? What questions threw you? Who do you
              need to follow up with?
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: InterviewPrepMessage }) {
  const isUser = message.role === "user";
  return (
    <li className="flex flex-col gap-1">
      <div
        className={`flex items-baseline gap-2 text-[10px] ${
          isUser ? "justify-end" : "justify-start"
        }`}
      >
        <span className="text-neutral-500">{isUser ? "You" : "Coach"}</span>
        <span className="text-neutral-700">{timeLabel(message.at)}</span>
      </div>
      <div
        className={`whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? "ml-8 bg-accent-300/10 text-neutral-100 ring-1 ring-accent-300/30"
            : "mr-8 bg-neutral-900/60 text-neutral-200 ring-1 ring-neutral-800"
        }`}
      >
        {message.content}
      </div>
      {message.retrieved_unit_ids && message.retrieved_unit_ids.length > 0 ? (
        <p className="ml-2 text-[10px] text-neutral-600">
          Sourced {message.retrieved_unit_ids.length} career units
        </p>
      ) : null}
    </li>
  );
}

function TypingIndicator() {
  return (
    <li className="flex items-center gap-2 text-xs text-neutral-500">
      <Loader2 size={14} className="animate-spin text-accent-300" />
      <span>Coach is thinking…</span>
    </li>
  );
}

function timeLabel(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function dateLabel(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Aliased default export for the lazy route.
export { InterviewPrep as InterviewPrepRoute };
